import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import classNames from 'classnames/bind';
import qs from 'qs';
import InputEmail from 'components/InputEmail/InputEmail';
import PrefixSelect, {
  ICountryPrefix,
} from 'components/PhoneInput/PrefixSelect/PrefixSelect';
import Icon from 'components/Icon/Icon';
import { SvgIcons } from 'common/consts/svgIcons';
import { getErrorObject, REGEXP_EMAIL } from 'common/helpers/valdiation';
import styles from './PasswordReset.module.scss';
import http from 'app/http';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { formProgression, formError } from 'common/helpers/Gtm';

type Props = {
  Model: any;
  PageData: any;
  disableSubmit: any;
  setDisableSubmit: any;
  onSubmit: (data: any) => any;
  onCancel: () => void;
};

enum RESET_MODE {
  PASSWORD = 'Password',
  LOGIN = 'Login',
}

const getDefaultPrefix = (prefixes: ICountryPrefix[]) =>
  (prefixes || []).find((prefix) => prefix.Selected);

const PasswordReset = ({ Model, PageData, disableSubmit, setDisableSubmit, onSubmit, onCancel }: Props) => {
  const [content, setContent] = useState<any>({ Model, PageData });
  const [resetMode, setResetMode] = useState<string>(RESET_MODE.PASSWORD);
  const defaultPrefix = useMemo(
    () => getDefaultPrefix(Model?.ResetFormModel?.CountryPrefixDropDown),
    [Model?.ResetFormModel?.CountryPrefixDropDown],
  );
  const [IsDisableRecaptchaValue, setIsDisableRecaptchaValue] = useState(content?.Model?.ResetFormModel?.IsDisableRecaptcha);
  const {
    control,
    register,
    formState: { isValid },
    handleSubmit,
    getFieldState,
  } = useForm({
    defaultValues: {
      ResetFormModel: {
        loginReset: content?.Model?.ResetFormModel?.loginReset,
        ResetEmailAddress: '',
        CountryPrefixCode: defaultPrefix?.Value,
        MobileNumber: '',
        TimeOffsetInMinutes:
          content?.Model?.ResetFormModel?.TimeOffsetInMinutes || 60,
        IsDisableRecaptcha: content?.Model?.ResetFormModel?.IsDisableRecaptcha,
        RecaptchaResponseToken: ''
      },
      reset: RESET_MODE.PASSWORD,
    },
    mode: 'all',
  });

  const mobileNumberState = getFieldState('ResetFormModel.MobileNumber');
  const resetEmailAddressState = getFieldState('ResetFormModel.ResetEmailAddress');
  useEffect(() => {
    if (mobileNumberState.error !== undefined) {
      formError('LoginReset','MobileNumber','error_Incorrect_Mobile_Number');
    }
    if (resetEmailAddressState.error !== undefined) {
      formError('PasswordReset','EmailAddress','error_email_is_not_recognized_or_registered');
    }
  }, [mobileNumberState,resetEmailAddressState]);

  const notExistEmailError = useMemo(() => {
    if (content?.Model?.EmailAddressNotExists) {
      formError('PasswordReset','EmailAddress','error_Email_address_doesnt_exist');
      return getErrorObject(content?.PageData?.EmailAddressNotExistsLabel);
    }
    return false;
  }, [
    content?.Model?.EmailAddressNotExists,
    content?.PageData?.EmailAddressNotExistsLabel,
  ]);

  const onModeChange = (e: any) => {
    setResetMode(e.target.value);
  };

  const { executeRecaptcha } = useGoogleReCaptcha(); // Recaptcha v3

  useEffect(() => {
    setContent({ Model, PageData });
  }, [Model, PageData]);

  useEffect(() => {
    if (content?.Model?.ResetFormModel?.loginReset) {
      setResetMode(RESET_MODE.LOGIN);
    }
  }, [content?.Model]);  
  
  const handleFormSubmit = async (data: any) => {
    data.ResetFormModel.IsDisableRecaptcha = IsDisableRecaptchaValue;
    data.ResetFormModel.CountryPrefixCode = data.ResetFormModel.CountryPrefixCode || defaultPrefix?.Value;    
    if (IsDisableRecaptchaValue) {
      onSubmit(data);
    } else {
      if (!executeRecaptcha) {
        return;
      }
      const token = await executeRecaptcha();

      if (token) {
        data.ResetFormModel = {
          ...data.ResetFormModel,
          RecaptchaResponseToken: token,
        };
        onSubmit(data);
      } else {
        onSubmit(data);
      }
    }    
  };

  const onFocusOutResetPassword = async (e:any) => {
    formProgression(e,'ResetPassword','EmailAddress')

  }

  return (
    <form
      id="resetForm"
      onSubmit={handleSubmit(handleFormSubmit)}
      className={styles.wrapper}
    >
      <div className={styles.heading}>
        {PageData?.ForgotPasswordOrEmailLabel}
      </div>
      <input
        {...register('ResetFormModel.loginReset')}
        type="hidden"
        value={content?.Model?.ResetFormModel?.loginReset}
      />
      <input
        {...register('ResetFormModel.TimeOffsetInMinutes')}
        type="hidden"
        value={content?.Model?.ResetFormModel?.TimeOffsetInMinutes}
      />
      <div className={styles.content}>
        {content?.PageData?.ForgotPasswordOrEmailInfoText && (
          <p className={styles.text}>
            {content?.PageData?.ForgotPasswordOrEmailInfoText}
          </p>
        )}
        {content?.Model?.ShowLoginReset && (
          <div className="toggle">
            <input
              {...register('reset')}
              type="radio"
              name="reset"
              id="Password"
              value={RESET_MODE.PASSWORD}
              checked={resetMode === RESET_MODE.PASSWORD}
              onChange={onModeChange}
            />
            <label htmlFor="Password">
              {content?.PageData?.ResetPasswordText}
            </label>
            <input
              {...register('reset')}
              type="radio"
              name="reset"
              id="Login"
              value={RESET_MODE.LOGIN}
              checked={resetMode === RESET_MODE.LOGIN}
              onChange={onModeChange}
            />
            <label htmlFor="Login">{content?.PageData?.ResetLoginText}</label>
          </div>
        )}
        {resetMode === RESET_MODE.PASSWORD && (
          <div className={styles.typeContent}>
            <Controller
              control={control}
              name={'ResetFormModel.ResetEmailAddress'}
              render={({
                field: { onChange, name, value },
                fieldState: { error, isDirty, invalid },
              }) => (
                <InputEmail
                  label={content?.PageData?.RegisteredEmailAddressLabel}
                  name={name}
                  value={value}
                  isValid={isDirty && !error}
                  // error={error || notExistEmailError}
                  onChange={onChange}
                  onFocusOut = {(e)=>onFocusOutResetPassword(e)}
                />
              )}
              rules={{
                required: true,
                pattern: {
                  value: REGEXP_EMAIL,
                  message: content?.PageData?.EmailErrorMessage,
                },
              }}
            />
            {content?.Model?.showRecaptchaPasswordError && (
              <span className={styles.error}>
                {PageData?.ReCaptchaErrorMessage}
              </span>
            )}
            <div className={styles.ctaWrapper}>
              <button
                type="submit"
                className={classNames(
                  styles.submitBtn,
                  (!disableSubmit &&isValid) && styles.active,
                )}
                disabled={disableSubmit || !isValid}
              >
                {content?.PageData?.ResetPasswordButtonText}
              </button>
            </div>
          </div>
        )}
        {resetMode === RESET_MODE.LOGIN && (
          <div className={styles.typeContent}>
            <div className={classNames(styles.mobileNumber, mobileNumberState.error && styles.hasError)}>
              <label className={styles.mobileNumberLabel}>
                {content?.PageData?.MobileNumberLabel}
              </label>
              <div className={classNames(styles.mobileNumberWrapper, mobileNumberState.error && styles.hasError)}>
                <Controller
                  control={control}
                  name={'ResetFormModel.CountryPrefixCode'}
                  render={({ field: { onChange, name, value } }) => (
                    <PrefixSelect
                      name={name}
                      value={value}
                      prefixes={
                        content?.Model?.ResetFormModel?.CountryPrefixDropDown
                      }
                      isBolded
                      className={styles.mobileNumberSelect}
                      onChange={onChange}
                    />
                  )}
                />
                <input
                  {...register('ResetFormModel.MobileNumber', {
                    required: true,
                    minLength: {
                      value: content?.Model?.MobilePhoneNumberMinLength,
                      message: content?.PageData?.IncorrectMobileNumberLabel,
                    },
                    maxLength: {
                      value: content?.Model?.MobilePhoneNumberMaxLength,
                      message: content?.PageData?.IncorrectMobileNumberLabel,
                    }
                  })}
                  type="number"
                  className={styles.mobileNumberInput}
                  onBlur = {(e)=>formProgression(e,'LoginReset','MobileNumber')}
                />
                {mobileNumberState.isDirty && !mobileNumberState.invalid && (
                  <Icon
                    type={SvgIcons.SUCCESS}
                    width={24}
                    className={styles.stateIcon}
                  />
                )}
                {mobileNumberState.error && (
                  <Icon
                    type={SvgIcons.ERROR}
                    width={16}
                    className={styles.stateIcon}
                  />
                )}
              </div>
              {mobileNumberState.error && (
                <span className={styles.error}>
                  {mobileNumberState.error?.message}
                </span>
              )}
              {content?.Model?.IncorrectMobileNumber && (
                <span className={styles.error}>
                  {PageData?.IncorrectMobileNumberLabel}
                </span>
              )}

              {content?.Model?.PhoneNumberNotExists && (
                <span className={styles.error}>
                  {PageData?.MobileNumberNotFoundLabel}
                </span>
              )}
              {content?.Model?.MultipleAccountsPresent && (
                <span className={styles.error}>
                  {PageData?.MultipleAccountsFoundLabel}
                </span>
              )}
              {content?.Model?.showRecaptchaLoginError && (
                <span className={styles.error}>
                  {PageData?.ReCaptchaErrorMessage}
                </span>
              )}
            </div>
            <div className={styles.ctaWrapper}>
              <button
                className={classNames(
                  styles.submitBtn,
                  (!disableSubmit &&isValid) && styles.active,
                )}
                disabled={disableSubmit || !isValid}
              >
                {content?.PageData?.SendCodeText}
              </button>
            </div>
          </div>
        )}
        <button onClick={onCancel} className={styles.cancelBtn}>
          {content?.PageData?.CancelButtonText}
        </button>
      </div>
    </form>
  );
};

export default PasswordReset;
